/* Brand-accurate icons for every integration in the Studio/Connections
 * surface. Uses Simple Icons via `react-icons/si` — MIT-licensed SVG
 * marks kept current upstream; we render them in their official brand
 * colour by default so connector tiles look like the real product,
 * not a generic emoji or initials placeholder.
 *
 * Per Simple Icons + each brand's usage policy: small, in-product,
 * unmodified marks for the purpose of identifying the third-party
 * service are permitted. Keep aspect ratio; never recolour the
 * coloured variant.
 */

import {
  SiGmail,
  SiGooglecalendar,
  SiGoogledrive,
  SiGoogleforms,
  SiHubspot,
  SiSalesforce,
  SiZoho,
  SiWhatsapp,
  SiSlack,
  SiTelegram,
  SiCalendly,
  SiDropbox,
  SiBox,
  SiAsana,
  SiTrello,
  SiLinear,
  SiJira,
  SiStripe,
  SiRazorpay,
  SiPaypal,
  SiShopify,
  SiWoocommerce,
  SiNotion,
  SiAirtable,
  SiConfluence,
  SiTypeform,
  SiAutodesk,
  SiSketchup,
  SiTrimble,
  SiAutocad,
  SiQuickbooks,
  SiXero,
} from "react-icons/si";
import { Mail, Calendar, Briefcase, MessageSquare, Folder, ListTodo, CreditCard, ShoppingBag, NotebookText, ClipboardList, Building2, Calculator, FileSignature } from "lucide-react";
import type { IconType } from "react-icons";
import type { LucideIcon } from "lucide-react";
import type { IntegrationGroup } from "../data/agenticIntegrations";

/** Single canonical slug per integration. Mapped to a real brand icon
 *  where one exists; falls back to a category icon otherwise. */
export type BrandSlug =
  | "gmail" | "outlook" | "imap"
  | "google-calendar" | "outlook-calendar" | "calendly"
  | "hubspot" | "pipedrive" | "salesforce" | "zoho-crm" | "freshsales"
  | "whatsapp" | "slack" | "telegram" | "microsoft-teams"
  | "google-drive" | "dropbox" | "onedrive" | "box"
  | "asana" | "trello" | "linear" | "monday" | "jira"
  | "stripe" | "razorpay" | "paypal"
  | "shopify" | "woocommerce" | "magento"
  | "notion" | "airtable" | "confluence"
  | "typeform" | "google-forms" | "jotform"
  | "autocad" | "sketchup" | "revit" | "3d-design"
  | "quickbooks" | "xero" | "tally"
  | "docusign" | "adobe-sign" | "zoho-sign";

// Direct mapping — first the brand-icon, second its official colour.
// `null` means we fall back to a Lucide category glyph (the brand is
// either too generic or not in Simple Icons).
const BRAND: Record<BrandSlug, { icon: IconType | null; color: string | null }> = {
  "gmail":            { icon: SiGmail,            color: "#EA4335" },
  "outlook":          { icon: null,               color: "#0078D4" },
  "imap":             { icon: null,               color: null      },

  "google-calendar":  { icon: SiGooglecalendar,   color: "#4285F4" },
  "outlook-calendar": { icon: null,               color: "#0078D4" },
  "calendly":         { icon: SiCalendly,         color: "#006BFF" },

  "hubspot":          { icon: SiHubspot,          color: "#FF7A59" },
  "pipedrive":        { icon: null,               color: "#1A1A1A" },
  "salesforce":       { icon: SiSalesforce,       color: "#00A1E0" },
  "zoho-crm":         { icon: SiZoho,             color: "#E42527" },
  "freshsales":       { icon: null,               color: "#22C55E" },

  "whatsapp":         { icon: SiWhatsapp,         color: "#25D366" },
  "slack":            { icon: SiSlack,            color: "#4A154B" },
  "telegram":         { icon: SiTelegram,         color: "#26A5E4" },
  "microsoft-teams":  { icon: null,               color: "#5059C9" },

  "google-drive":     { icon: SiGoogledrive,      color: "#1FA463" },
  "dropbox":          { icon: SiDropbox,          color: "#0061FF" },
  "onedrive":         { icon: null,               color: "#0078D4" },
  "box":              { icon: SiBox,              color: "#0061D5" },

  "asana":            { icon: SiAsana,            color: "#F06A6A" },
  "trello":           { icon: SiTrello,           color: "#0079BF" },
  "linear":           { icon: SiLinear,           color: "#5E6AD2" },
  "monday":           { icon: null,               color: "#FF3D57" },
  "jira":             { icon: SiJira,             color: "#0052CC" },

  "stripe":           { icon: SiStripe,           color: "#635BFF" },
  "razorpay":         { icon: SiRazorpay,         color: "#3395FF" },
  "paypal":           { icon: SiPaypal,           color: "#003087" },

  "shopify":          { icon: SiShopify,          color: "#95BF47" },
  "woocommerce":      { icon: SiWoocommerce,      color: "#7F54B3" },
  "magento":          { icon: null,               color: "#EE672F" },

  "notion":           { icon: SiNotion,           color: "#000000" },
  "airtable":         { icon: SiAirtable,         color: "#18BFFF" },
  "confluence":       { icon: SiConfluence,       color: "#172B4D" },

  "typeform":         { icon: SiTypeform,         color: "#262627" },
  "google-forms":     { icon: SiGoogleforms,      color: "#7248B9" },
  "jotform":          { icon: null,               color: "#FF6100" },

  "autocad":          { icon: SiAutocad,          color: "#E51050" },
  "sketchup":         { icon: SiSketchup,         color: "#005F9E" },
  "revit":            { icon: SiAutodesk,         color: "#0696D7" },
  "3d-design":        { icon: SiTrimble,          color: "#0063A3" },

  "quickbooks":       { icon: SiQuickbooks,       color: "#2CA01C" },
  "xero":             { icon: SiXero,             color: "#13B5EA" },
  "tally":            { icon: null,               color: "#1F4E8C" },

  "docusign":         { icon: null,               color: "#FFCC22" },
  "adobe-sign":       { icon: null,               color: "#EC1C24" },
  "zoho-sign":        { icon: SiZoho,             color: "#E42527" },
};

const GROUP_FALLBACK: Record<IntegrationGroup, LucideIcon> = {
  "Email":               Mail,
  "Calendar":            Calendar,
  "CRM":                 Briefcase,
  "Messaging":           MessageSquare,
  "Files · Storage":     Folder,
  "Project · Tasks":     ListTodo,
  "Payments":            CreditCard,
  "E-commerce · Catalog": ShoppingBag,
  "Productivity":        NotebookText,
  "Forms · Capture":     ClipboardList,
  "CAD · Design":        Building2,
  "Accounting":          Calculator,
  "Signing":             FileSignature,
};

interface BrandIconProps {
  slug: BrandSlug | null | undefined;
  group?: IntegrationGroup;
  size?: number;
  /** If true, render the icon in its brand colour. False → currentColor. */
  brandColor?: boolean;
  className?: string;
  title?: string;
}

export function BrandIcon({
  slug, group, size = 18, brandColor = true, className, title,
}: BrandIconProps): JSX.Element {
  const entry = slug ? BRAND[slug] : null;
  if (entry?.icon) {
    const Icon = entry.icon;
    const useBrand = brandColor && !!entry.color;
    return (
      <Icon
        size={size}
        {...(useBrand && entry.color ? { color: entry.color } : {})}
        {...(className ? { className } : {})}
        {...(title ? { title } : {})}
        aria-hidden={!title}
      />
    );
  }
  // Fallback to a calm Lucide category glyph in currentColor.
  const Fallback = group ? GROUP_FALLBACK[group] : Mail;
  return <Fallback size={size} className={className} aria-label={title} />;
}
